import React from 'react';
import { Armchair, CalendarClock, Clock, Copy, Hourglass, Link2, QrCode, UserCheck } from 'lucide-react';
import { buildQrCodeFileName } from '../../utils/adminDashboardHelpers.ts';

type QrCodeModalState = {
  title: string;
  link: string;
  fileName: string;
};

type DashboardSummary = {
  available: number;
  occupied: number;
  reservationsToday: number;
};

type DashboardSummaryPanelProps = {
  environmentName?: string;
  usarMapaMesas: boolean;
  dashboardSummary: DashboardSummary;
  dashboardWaitingCount: number;
  reservationsFeatureEnabled: boolean;
  waitlistFeatureEnabled: boolean;
  reservationLink: string;
  waitlistLink: string;
  restaurantSlug: string;
  handleOpenQrCodeModal: (modalState: QrCodeModalState) => void;
  handleCopyReservationLink: () => void;
  handleCopyWaitlistLink: () => void;
};

const DashboardSummaryPanel: React.FC<DashboardSummaryPanelProps> = ({
  environmentName,
  usarMapaMesas,
  dashboardSummary,
  dashboardWaitingCount,
  reservationsFeatureEnabled,
  waitlistFeatureEnabled,
  reservationLink,
  waitlistLink,
  restaurantSlug,
  handleOpenQrCodeModal,
  handleCopyReservationLink,
  handleCopyWaitlistLink,
}) => (
  <section className="room-summary-panel">
    {environmentName && <p className="room-summary-environment">Ocupação de {environmentName}</p>}
    <div className="room-summary">
      <div className="room-summary-item is-available">
        <div className="room-summary-icon">
          <Armchair size={20} />
        </div>
        <div className="room-summary-body">
          <span>{usarMapaMesas ? 'Disponíveis' : 'Cadeiras disponíveis'}</span>
          <strong>{dashboardSummary.available}</strong>
        </div>
      </div>
      <div className="room-summary-item is-occupied">
        <div className="room-summary-icon">
          <UserCheck size={20} />
        </div>
        <div className="room-summary-body">
          <span>
            {usarMapaMesas
              ? (dashboardSummary.occupied === 1 ? 'Ocupada' : 'Ocupadas')
              : (dashboardSummary.occupied === 1 ? 'Cadeira ocupada' : 'Cadeiras ocupadas')}
          </span>
          <strong>{dashboardSummary.occupied}</strong>
        </div>
      </div>
      <div
        className="room-summary-item is-total"
        title={usarMapaMesas
          ? 'Mesas com cliente aguardado e pessoas na fila de espera'
          : 'Pessoas na fila de espera'}
      >
        <div className="room-summary-icon">
          <Clock size={20} />
        </div>
        <div className="room-summary-body">
          <span>Aguardando</span>
          <strong>{dashboardWaitingCount}</strong>
        </div>
      </div>
      {reservationsFeatureEnabled && (
        <div className="room-summary-item is-reservations">
          <div className="room-summary-icon">
            <CalendarClock size={20} />
          </div>
          <div className="room-summary-body">
            <span>Reservas do Dia</span>
            <strong>{dashboardSummary.reservationsToday}</strong>
          </div>
        </div>
      )}
    </div>

    <div className={`room-summary-links ${reservationsFeatureEnabled && waitlistFeatureEnabled ? 'has-two-links' : ''}`}>
      {reservationsFeatureEnabled && (
        <div className="room-summary-link" title={reservationLink}>
          <div className="room-summary-link-label">
            <Link2 size={16} />
            <span>Link de Reservas</span>
          </div>
          <a
            className="room-summary-link-url"
            href={reservationLink}
            target="_blank"
            rel="noreferrer"
          >
            {reservationLink}
          </a>
          <div className="room-summary-link-actions">
            <button
              type="button"
              className="room-summary-link-copy"
              onClick={() => handleOpenQrCodeModal({
                title: 'QR Code do link de reservas',
                link: reservationLink,
                fileName: buildQrCodeFileName('reservas', restaurantSlug),
              })}
              aria-label="Ver QR Code do link de reservas"
              title="Ver QR Code do link de reservas"
            >
              <QrCode size={16} />
            </button>
            <button
              type="button"
              className="room-summary-link-copy"
              onClick={handleCopyReservationLink}
              aria-label="Copiar link de reservas"
              title="Copiar link de reservas"
            >
              <Copy size={16} />
            </button>
          </div>
        </div>
      )}

      {waitlistFeatureEnabled && (
        <div className="room-summary-link" title={waitlistLink}>
          <div className="room-summary-link-label">
            <Hourglass size={16} />
            <span>Link da Fila de Espera</span>
          </div>
          <a
            className="room-summary-link-url"
            href={waitlistLink}
            target="_blank"
            rel="noreferrer"
          >
            {waitlistLink}
          </a>
          <div className="room-summary-link-actions">
            <button
              type="button"
              className="room-summary-link-copy"
              onClick={() => handleOpenQrCodeModal({
                title: 'QR Code do link da fila de espera',
                link: waitlistLink,
                fileName: buildQrCodeFileName('fila', restaurantSlug),
              })}
              aria-label="Ver QR Code do link da fila de espera"
              title="Ver QR Code do link da fila de espera"
            >
              <QrCode size={16} />
            </button>
            <button
              type="button"
              className="room-summary-link-copy"
              onClick={handleCopyWaitlistLink}
              aria-label="Copiar link da fila de espera"
              title="Copiar link da fila de espera"
            >
              <Copy size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  </section>
);

export default DashboardSummaryPanel;
